import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../services/api';
import { useOAuth } from '../context/OAuthContext';
import { CITIES } from '../constants/cities';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import BBCodeText from '../components/shared/BBCodeText';
import ApplicationConfirmation from '../components/shared/ApplicationConfirmation';
import './Careers.css';

const Careers = () => {
  const { currentUser: oauthUser, login } = useOAuth();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    residentialAddress: '',
    city: '',
    routingNumber: '',
    portfolio: '',
    serverRecordLink: '',
    characterStatsLink: '',
    coverLetter: '',
    additionalRoles: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadJobs();

    const savedApplicationData = sessionStorage.getItem('pendingApplication');
    if (savedApplicationData && oauthUser && oauthUser.activeCharacterId) {
      try {
        const data = JSON.parse(savedApplicationData);
        setSelectedJob(data.selectedJob);
        setFormData(data.formData);
        sessionStorage.removeItem('pendingApplication');
      } catch (err) {
        console.error('Error loading saved application data:', err);
      }
    }
  }, [oauthUser]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const [jobsResponse, categoriesResponse] = await Promise.all([
        jobsAPI.getAll(false),
        jobsAPI.getCategories(false)
      ]);
      if (jobsResponse.success) {
        setJobs(jobsResponse.data);
      }
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      setError('Failed to load job positions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = selectedCategory === 'all'
    ? jobs
    : jobs.filter(job => String(job.category_id) === selectedCategory);

  const categoryCount = (categoryId) => jobs.filter(
    job => String(job.category_id) === String(categoryId)
  ).length;

  const handleJobSelect = (job) => {
    if (!oauthUser || !oauthUser.activeCharacterId) {
      setSelectedJob(job);
      setShowLoginModal(true);
      setError('Please log in with OAuth and select a character before applying.');
      return;
    }

    setSelectedJob(job);

    if (oauthUser && oauthUser.activeCharacterId) {
      const activeCharacter = oauthUser.characters?.find(
        c => c.id === oauthUser.activeCharacterId || c.isActive
      );

      let formattedDateOfBirth = '';
      const dateValue = activeCharacter?.dateOfBirth || activeCharacter?.date_of_birth;
      if (dateValue) {
        if (typeof dateValue === 'string') {
          formattedDateOfBirth = dateValue.split('T')[0].split(' ')[0];
        } else if (dateValue instanceof Date) {
          formattedDateOfBirth = dateValue.toISOString().split('T')[0];
        }
      }

      setFormData({
        firstName: activeCharacter?.firstname || '',
        lastName: activeCharacter?.lastname || '',
        email: activeCharacter?.email || '',
        phone: activeCharacter?.phone || '',
        dateOfBirth: formattedDateOfBirth,
        residentialAddress: activeCharacter?.residentialAddress || activeCharacter?.residential_address || activeCharacter?.address || '',
        city: activeCharacter?.city || '',
        routingNumber: activeCharacter?.routingNumber || activeCharacter?.routing_number || '',
        portfolio: activeCharacter?.portfolio || '',
        serverRecordLink: '',
        characterStatsLink: '',
        coverLetter: '',
        additionalRoles: []
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        residentialAddress: '',
        city: '',
        routingNumber: '',
        portfolio: '',
        serverRecordLink: '',
        characterStatsLink: '',
        coverLetter: '',
        additionalRoles: []
      });
    }
    setError('');
    setSuccess(false);
    setSubmittedApplication(null);
  };

  const handleChange = (e) => {
    const { name, value, options } = e.target;
    if (name === 'additionalRoles') {
      const selectedValues = Array.from(options)
        .filter(option => option.selected)
        .map(option => parseInt(option.value));
      setFormData(prev => ({
        ...prev,
        [name]: selectedValues
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!oauthUser || !oauthUser.activeCharacterId) {
      setShowLoginModal(true);
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const submitFormData = new FormData();
      submitFormData.append('positionId', selectedJob.id);
      submitFormData.append('firstName', formData.firstName.trim());
      submitFormData.append('lastName', formData.lastName.trim());
      submitFormData.append('email', formData.email.trim());
      if (formData.phone) {
        submitFormData.append('phone', formData.phone.trim());
      }
      if (formData.dateOfBirth) {
        submitFormData.append('dateOfBirth', formData.dateOfBirth);
      }
      if (formData.residentialAddress) {
        submitFormData.append('residentialAddress', formData.residentialAddress.trim());
      }
      if (formData.city) {
        submitFormData.append('city', formData.city.trim());
      }
      if (formData.routingNumber) {
        submitFormData.append('routingNumber', formData.routingNumber.trim());
      }
      if (formData.portfolio) {
        submitFormData.append('portfolio', formData.portfolio.trim());
      }
      if (formData.serverRecordLink) {
        submitFormData.append('serverRecordLink', formData.serverRecordLink.trim());
      }
      if (formData.characterStatsLink) {
        submitFormData.append('characterStatsLink', formData.characterStatsLink.trim());
      }
      if (formData.coverLetter) {
        submitFormData.append('coverLetter', formData.coverLetter.trim());
      }
      if (formData.additionalRoles && formData.additionalRoles.length > 0) {
        submitFormData.append('additionalRoles', JSON.stringify(formData.additionalRoles));
      }

      const response = await jobsAPI.submitApplication(submitFormData);

      if (response.success && response.data?.application_token) {
        setSuccess(true);
        setSubmittedApplication(response.data);
        setSelectedJob(null);
      } else {
        setError(response.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !success) {
    return (
      <div className="careers-page">
        <div className="careers-container">
          <h1>Careers</h1>
          <p>Loading available positions...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="careers-page-wrapper">
      <ResponsiveNavbar
        menuItems={[
          { type: 'link', label: 'Veloura', to: '/' },
          { type: 'link', label: 'Shop', to: '/shop' },
          { type: 'link', label: 'Team', to: '/team' },
          { type: 'link', label: 'Careers', to: '/careers', active: true },
          { type: 'link', label: 'Partners', to: '/partners' },
          { type: 'link', label: 'News', to: '/news' },
          { type: 'link', label: 'Account', to: '/account' }
        ]}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />
      <div className="careers-page">
        <div className={`careers-container ${success ? 'careers-container--confirmation' : ''}`}>
        {!selectedJob && !success && (
          <>
            <section className="careers-hero">
              <div>
                <p className="careers-eyebrow">Careers at Veloura</p>
                <h1>{jobs.length} Career {jobs.length === 1 ? 'Option' : 'Options'}</h1>
                <p className="careers-intro">
                  Find the role that fits your craft and help shape what Veloura becomes next.
                </p>
              </div>
              <div className="careers-hero-note">
                <span className="careers-note-mark" aria-hidden="true"></span>
                <div>
                  <strong>Creative work. Real impact.</strong>
                  <p>We welcome original thinkers, makers, and people who care about the details.</p>
                </div>
              </div>
            </section>

            <div className="careers-browser">
              <aside className="career-filters" aria-label="Filter career options">
                <p className="career-filter-label">Filter roles</p>
                <button
                  className={`career-filter-button ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('all')}
                >
                  <span>All Departments</span>
                  <span>{jobs.length}</span>
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`career-filter-button ${selectedCategory === String(category.id) ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(String(category.id))}
                  >
                    <span>{category.name}</span>
                    <span>{categoryCount(category.id)}</span>
                  </button>
                ))}
              </aside>

              <div className="jobs-list">
                <div className="career-results-heading">
                  <span>{filteredJobs.length} {filteredJobs.length === 1 ? 'role' : 'roles'}</span>
                  {selectedCategory !== 'all' && (
                    <button onClick={() => setSelectedCategory('all')}>Clear filter</button>
                  )}
                </div>
                {filteredJobs.length === 0 ? (
                  <div className="no-jobs">
                    <span className="empty-divider"></span>
                    <p>No career options match this filter right now.</p>
                  </div>
                ) : filteredJobs.map(job => (
                  <article key={job.id} className="job-row">
                    <div
                      className="job-visual"
                      style={job.background_image ? { backgroundImage: `url(${job.background_image})` } : undefined}
                      aria-hidden="true"
                    >
                      {!job.background_image && (
                        <span>{job.title.match(/[A-Za-z0-9]/)?.[0]?.toUpperCase() || 'V'}</span>
                      )}
                    </div>
                    <div className="job-summary">
                      <div className="job-title-line">
                        <h2>{job.title}</h2>
                      </div>
                      <BBCodeText
                        className="job-description-preview"
                        text={job.description || 'Join Veloura and help us deliver thoughtful, distinctive work.'}
                      />
                      <div className="job-meta">
                        <span>{job.category_name || 'General'}</span>
                        <span>{job.location || 'Los Santos'}</span>
                        <span>{job.employment_type || 'Full-time'}</span>
                      </div>
                    </div>
                    <div
                      className="job-openings"
                      aria-label={`${job.openings || 1} ${(job.openings || 1) === 1 ? 'opening' : 'openings'}`}
                    >
                      <strong>{job.openings || 1}</strong>
                      <span>{(job.openings || 1) === 1 ? 'opening' : 'openings'}</span>
                    </div>
                    <button className="apply-button" onClick={() => handleJobSelect(job)}>
                      Apply Now <span aria-hidden="true">-&gt;</span>
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
        {selectedJob && !success && (
          <div className="application-form-container">
            <button
              className="back-button"
              onClick={() => {
                setSelectedJob(null);
                setError('');
              }}
            >
              ← Back to Positions
            </button>
            <div className="selected-job-info">
              <h2>Apply for: {selectedJob.title}</h2>
            </div>
            <form className="application-form" onSubmit={handleSubmit}>
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">
                    Last Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  ((Discord)) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  placeholder="Discord username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ucpAccount">((UCP Account))</label>
                <input
                  type="text"
                  id="ucpAccount"
                  name="ucpAccount"
                  value={oauthUser?.username || ''}
                  readOnly
                  disabled={submitting}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={submitting}
                  >
                    <option value="">Select a city</option>
                    {CITIES.map(cityName => (
                      <option key={cityName} value={cityName}>{cityName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="residentialAddress">Residential Address</label>
                <input
                  type="text"
                  id="residentialAddress"
                  name="residentialAddress"
                  value={formData.residentialAddress}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Enter street address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="routingNumber">Routing Number</label>

                <input
                  type="text"
                  id="routingNumber"
                  name="routingNumber"
                  value={formData.routingNumber}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Bank routing number"
                />
              </div>
              <div className="form-group">
                <label htmlFor="portfolio">Portfolio (Optional)</label>
                <textarea
                  id="portfolio"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  rows="4"
                  disabled={submitting}
                  placeholder="Links to your portfolio, previous work, or any relevant examples"
                />
              </div>
              <div className="form-group">
                <label htmlFor="serverRecordLink">Server Record Link</label>
                <input
                  type="url"
                  id="serverRecordLink"
                  name="serverRecordLink"
                  value={formData.serverRecordLink}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="https://ucp.gta.world/view/record/xxxxxx"
                />
                <span className="form-hint">UCP &gt; Profile &gt; Server Record &gt; Share Record</span>
              </div>
              <div className="form-group">
                <label htmlFor="characterStatsLink">Character Stats Link</label>

                <input
                  type="url"
                  id="characterStatsLink"
                  name="characterStatsLink"
                  value={formData.characterStatsLink}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="https://ucp.gta.world/view/stats/xxxxxx"
                />
                <span className="form-hint">UCP &gt; Character Name &gt; Stats &gt; Share Stats</span>
              </div>
              <div className="application-privacy-note" role="note">
                <p><strong>Note:</strong> Your Server Record and Character Stats will only be viewable by Faction Leadership.</p>
              </div>
              <div className="form-group">
                <label htmlFor="coverLetter">Cover Letter</label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows="6"
                  disabled={submitting}
                  placeholder="Tell us why you're interested in this position..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="additionalRoles">Other Roles of Interest (Optional)</label>
                <select
                  id="additionalRoles"
                  name="additionalRoles"
                  multiple
                  value={formData.additionalRoles.map(String)}
                  onChange={handleChange}
                  disabled={submitting}
                  className="form-multiselect"
                  size="4"
                >
                  {jobs
                    .filter(job => job.id !== selectedJob.id && job.active)
                    .map(job => (
                      <option key={job.id} value={String(job.id)}>
                        {job.title}
                      </option>
                    ))}
                </select>
                <span className="form-hint">Hold Ctrl to select multiple roles</span>
              </div>
              <div className="application-notes">
                <h3>Important Notes</h3>
                <ul>
                  <li>Applications may be denied if they are incomplete, contain poor grammar, or show a lack of effort.</li>
                  <li>AI-generated responses will result in immediate denial and may lead to a ban from submitting future applications.</li>
                </ul>
                <p className="discord-note">
                  It is <strong>Mandatory</strong> to join <a href="https://gtaw.link/veloura" target="_blank" rel="noopener noreferrer" className="portal-link">the portal</a> to be contacted via live chat to follow up on your application.
                </p>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => {
                    setSelectedJob(null);
                    setError('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>

              </div>

            </form>

          </div>

        )}

        {success && submittedApplication && (
          <ApplicationConfirmation
            key={submittedApplication.application_token}
            application={submittedApplication}
            onBackToCareers={() => {
              setSuccess(false);
              setSubmittedApplication(null);
              setSelectedJob(null);
            }}
          />
        )}

        {}
        {showLoginModal && (
          <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
            <div className="login-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Login Required</h3>

              <p>Please log in with OAuth and select a character before submitting an application.</p>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowLoginModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="login-button"
                  onClick={() => {
                    sessionStorage.setItem('pendingApplication', JSON.stringify({
                      selectedJob,
                      formData
                    }));
                    sessionStorage.setItem('redirectUrl', '/careers');
                    login('/careers');
                  }}
                >
                  Login to Continue
                </button>

              </div>

            </div>

          </div>

        )}
        </div>

      </div>

    </div>

  );
};

export default Careers;
